/**
 * What a create returns once and never again: the ingestion token and the
 * presigned bundle URL. The launch dialog hands them to the deployment view it
 * navigates to, which shows them once.
 *
 * Memory only, on purpose. Not the URL, not router state (the browser keeps
 * history state across reloads), not web storage, not the query cache. One
 * entry at most: a new handover drops the previous one, and the view forgets
 * its entry as soon as it has read it. A reload loses it, which is the same
 * "shown once" the API promises.
 */
export type CreatedLaunchSecrets = {
  ingestToken: string;
  /** The bundle's .env.secrets already carries the token. */
  inBundle: boolean;
  downloadUrl: string | null;
};

let pending: { deploymentId: string; secrets: CreatedLaunchSecrets } | null = null;

export function handOverCreatedLaunch(deploymentId: string, secrets: CreatedLaunchSecrets): void {
  pending = { deploymentId, secrets };
}

/** The secrets for this deployment, if they are still waiting. No side effect,
 *  so it is safe in a state initialiser that React may call twice. */
export function peekCreatedLaunch(deploymentId: string): CreatedLaunchSecrets | null {
  return pending?.deploymentId === deploymentId ? pending.secrets : null;
}

export function forgetCreatedLaunch(deploymentId: string): void {
  if (pending?.deploymentId === deploymentId) pending = null;
}

/** Drops whatever is waiting. Called whenever the session ends (sign-out,
 *  account deletion, a 401), so a token never outlives its user's session in
 *  this tab. */
export function clearCreatedLaunch(): void {
  pending = null;
}
